import type { Tutorial } from '../../shared/types.js';

/**
 * Signals that drive the launch dialog to the key-less free combo and pre-fill
 * the benchmark prompt. The dialog watches these `data-tutorial-launch-*`
 * attributes: the free agent + model make Launch reachable in a fresh org with
 * no provider keys, and the prompt means the user only has to click Launch with
 * nothing to type. Stamped on the single Launch step (shown the moment the
 * dialog opens) and kept through the click, so a late OpenCode catalog load
 * can't strand a half-applied switch with Launch disabled.
 */
/**
 * The pre-filled task. A ~one-minute all-core CPU benchmark is deliberate: it
 * keeps the freshly-launched devbox busy (an idle box gets suspended) so the
 * sandbox monitor has live CPU/memory/process data to show while we walk the
 * charts. Shared between the launch signal and the Launch step's preview card so
 * what we stamp and what we show the user never drift.
 */
const BENCHMARK_PROMPT =
  'Write and run a CPU benchmark that keeps all cores busy for about one minute, then summarize the results';

const LAUNCH_FREE_COMBO_SIGNALS = {
  'data-tutorial-launch-agent': 'opencode',
  'data-tutorial-launch-model': 'opencode/big-pickle',
  'data-tutorial-launch-prompt': BENCHMARK_PROMPT,
} as const;

/**
 * Hands-on tour of the sandbox monitor. Rather than assume the user already has
 * an active agent (an idle devbox gets suspended, so its charts sit empty), the
 * tour first launches a quick OpenCode + big-pickle run with a short CPU
 * benchmark prompt - the key-less free combo the launch tour uses - so the box
 * stays busy and the live charts actually move while we walk them. The launch
 * steps live on `/runs` and reuse the host launch dialog (see `launch-agent`);
 * clicking Launch routes to the new agent's workspace, where the remaining steps
 * anchor to the SandboxStatusBadge and SandboxMonitorPanel hooks.
 */
export const sandboxMonitorTutorial: Tutorial = {
  name: 'sandbox-monitor',
  title: 'Monitor an agent sandbox',
  description:
    "See how your agent's devbox is doing in real time: launch a quick agent that keeps its box busy, open the sandbox monitor from the header, and read the live CPU, memory, network, disk, and process charts.",
  category: 'agent-sessions',
  docPages: ['concepts/agents'],
  releasedAt: '2026-06-23',
  agenda: [
    'Launch a quick agent that keeps its sandbox busy',
    'Open the live sandbox monitor',
    'Read the CPU, memory, network, and disk charts',
    'See which processes are using the box',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Watch a sandbox work live',
      body: "We'll launch a quick agent and watch its devbox work in real time in the sandbox monitor. Let's start by opening the launch dialog.",
      path: '/runs',
    },
    {
      id: 'open-dialog',
      kind: 'action',
      trigger: 'click',
      title: 'Open the launch dialog',
      body: 'Start from the **New agent** button in the top-right.',
      target: '[aria-label="New agent"]',
      placement: 'right',
    },
    {
      id: 'launch',
      kind: 'action',
      trigger: 'click',
      title: 'Launch it',
      // Short summary - the passive note on the prompt carries the detail/why.
      body: 'Hit the highlighted **send** button to launch - the devbox spins up and the benchmark starts.',
      // The composer's send button (the arrow next to the prompt), which submits
      // the launch just like the footer button. Scoped inside the chat-input so
      // it's the one beside the prompt, not the footer Launch.
      target: '[role="dialog"] [data-tutorial-id="agent-chat-input"] [data-replay-target="send"]',
      placement: 'left',
      // Highlight the pre-filled prompt as a read-only reference (passive: the
      // user can't edit it) with the annotation explaining why it's there.
      notes: [
        {
          id: 'prompt-task',
          target: '[role="dialog"] [aria-label="Message input"]',
          title: 'Your task is set',
          body: 'A one-minute CPU benchmark, pre-filled - it keeps the new devbox busy so the sandbox monitor has live data to show. No need to edit it.',
          placement: 'top',
          passive: true,
        },
      ],
      // Stamp the free combo + prompt the moment the dialog opens (this is the
      // first in-dialog step) and keep them through the click, so a late catalog
      // load can't strand a half-applied switch with Launch disabled.
      htmlSignals: { ...LAUNCH_FREE_COMBO_SIGNALS },
    },
    {
      id: 'agent-working',
      kind: 'info',
      title: 'The agent gets to work',
      body: 'The agent is now running in its devbox - writing the benchmark and spiking the CPU across every core. That load is exactly what makes the sandbox monitor interesting, so let it run for a moment while we go watch the box.',
      target: '[data-tutorial-id="agent-chat-log"]',
      placement: 'left',
      // Default dimming spotlights the chat log (revealing it) while greying out
      // and locking the rest of the page, so this watch beat keeps focus on the
      // agent's work and the user can't click off into the UI mid-launch.
      // Hold here a while so the run actually starts spiking the CPU before we
      // move on to the live charts. While the timer runs the card hides Next and
      // shows a countdown ring, so the step can't be skipped early, then
      // advances on its own.
      autoAdvanceMs: 15000,
    },
    {
      id: 'open-monitor',
      kind: 'action',
      trigger: 'click',
      title: 'Open the sandbox monitor',
      body: 'The **Sandbox** badge in the header shows the devbox state at a glance - **Provisioning**, then **Active** once the CPU test runs. Click it to slide out the full monitor.',
      target: '[data-testid="sandbox-status-badge"]',
      placement: 'bottom',
      // The panel is a Radix Sheet that portals its content over the page, so a
      // raw click on the badge is not always observed - advance the moment the
      // panel appears instead (matches the agent-detail actions-menu step).
      advanceWhen: '[data-testid="sandbox-monitor-panel"]',
    },
    {
      id: 'warmup',
      kind: 'action',
      // The trigger is a formality - `advanceWhen` drives this step. Reading the
      // detail section while the box boots and streams its first metric sample
      // covers the provisioning + first-sample lag, so the chart step never
      // lands on an empty "Waiting for the first sample" panel.
      trigger: 'click',
      title: 'Sandbox details',
      body: "Up top you get the devbox's current state and id, plus an **Open in Runloop** link to jump straight to the box in the Runloop dashboard. It's still booting and running the benchmark - the charts below come alive the moment the first sample lands, usually just a few seconds. Hang tight; we'll move on automatically.",
      target: '[data-testid="sandbox-detail-section"]',
      placement: 'left',
      advanceWhen: '[data-testid="sandbox-monitor-section"][data-has-samples="true"]',
    },
    {
      id: 'cpu',
      kind: 'info',
      title: 'Read the live charts',
      body: 'These charts stream live as the agent works: **CPU** and **memory** against the box limits, plus **network** and **disk I/O**, with a dashed line marking the warning threshold. With the benchmark running you should see CPU climb - the line is usage over time and the sublabel shows the core limit; hover any point for the exact value. Memory, network, and disk read the same way.',
      target: '[data-testid="sandbox-monitor-cpu-chart"]',
      placement: 'left',
    },
    {
      id: 'processes',
      kind: 'info',
      title: 'What is running',
      body: 'The **Processes** list is a live, top-style breakdown of what is using the box. With the CPU test going, sort by **CPU** to see the benchmark jump to the top.',
      target: '[data-testid="sandbox-monitor-processes"]',
      placement: 'left',
    },
    {
      id: 'done',
      kind: 'info',
      title: "That's the monitor",
      body: 'Open the sandbox monitor any time a run feels slow or stuck and see exactly what the devbox is doing. Close the panel whenever you are done.',
    },
  ],
  related: [{ tutorialName: 'agent-detail', label: 'Tour the agent workspace' }],
};
